/**
 * Modern mega menu component for landing page subjects navigation.
 *
 * What: Full-width dropdown panel with multi-column grid layout for subjects/levels navigation.
 * Why: Provides world-class, scannable navigation experience with clear visual hierarchy.
 * When: Update when navigation structure changes or new levels are added.
 */
import { Link } from 'react-router-dom';
import { Box, Chip, Container, Grid, Typography } from '@mui/material';

import { colors, radii } from '@/components/landing/landingTokens';

interface MegaMenuItem {
  label: string;
  path: string;
  sublabel?: string;
  badge?: string;
  isHub?: boolean;
}

interface MegaMenuGroup {
  title: string;
  description?: string;
  items: MegaMenuItem[];
}

interface LandingMegaMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onEscapeKeyDown?: () => void;
  menuId?: string;
  triggerId?: string;
  groups: MegaMenuGroup[];
}

function LandingMegaMenu({
  isOpen,
  onClose,
  onEscapeKeyDown,
  menuId,
  triggerId,
  groups,
}: LandingMegaMenuProps) {
  const handleClose = (reason: string) => {
    if (reason === 'escape' && onEscapeKeyDown) {
      onEscapeKeyDown();
      return;
    }
    onClose();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      handleClose('escape');
    }
  };

  if (!isOpen) return null;

  return (
    <Box
      id={menuId}
      role="region"
      aria-label="Subjects and levels navigation"
      aria-labelledby={triggerId}
      sx={{
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        mt: 1,
        pointerEvents: 'auto',
        zIndex: theme => theme.zIndex.modal,
      }}
      onMouseLeave={() => handleClose('mouse')}
      onKeyDown={handleKeyDown}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            backgroundColor: colors.paper,
            borderRadius: radii.lg,
            border: `1px solid ${colors.border}`,
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
            overflow: 'hidden',
            animation: 'megaMenuSlideIn 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
            '@keyframes megaMenuSlideIn': {
              from: {
                opacity: 0,
                transform: 'translateY(-8px)',
              },
              to: {
                opacity: 1,
                transform: 'translateY(0)',
              },
            },
          }}
        >
          <Grid container spacing={0}>
            {groups.map((group, index) => (
              // Keep each column as a labeled region for screen-reader scanning.
              <Grid
                item
                xs={12}
                md={12 / groups.length}
                key={group.title}
                sx={{
                  borderRight:
                    index < groups.length - 1
                      ? { xs: 'none', md: `1px solid ${colors.border}` }
                      : 'none',
                  borderBottom:
                    index < groups.length - 1
                      ? { xs: `1px solid ${colors.border}`, md: 'none' }
                      : 'none',
                }}
              >
                <Box
                  component="section"
                  aria-label={group.title}
                  sx={{
                    p: { xs: 3, md: 4 },
                  }}
                >
                  {/* Group Header */}
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="overline"
                      sx={{
                        fontSize: '0.6875rem',
                        fontWeight: 800,
                        letterSpacing: 1.2,
                        color: colors.accentOrange,
                        textTransform: 'uppercase',
                        display: 'block',
                        mb: 0.5,
                      }}
                    >
                      {group.title}
                    </Typography>
                    {group.description && (
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '0.8125rem',
                          color: colors.inkSubtle,
                          display: 'block',
                        }}
                      >
                        {group.description}
                      </Typography>
                    )}
                  </Box>

                  {/* Menu Items */}
                  <Box
                    component="nav"
                    aria-label={`${group.title} links`}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.75,
                    }}
                  >
                    {group.items.map(item => (
                      <Box
                        key={item.path}
                        component={Link}
                        to={item.path}
                        onClick={() => handleClose('selection')}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          px: 2,
                          py: 1.5,
                          borderRadius: radii.sm,
                          textDecoration: 'none',
                          backgroundColor: 'transparent',
                          outline: '2px solid transparent',
                          outlineOffset: 2,
                          transition: 'all 0.2s ease',
                          '&:focus-visible': {
                            outline: `3px solid ${colors.accentCobalt}`,
                            outlineOffset: 2,
                          },
                          '&:hover': {
                            backgroundColor: colors.backgroundAlt,
                            transform: 'translateX(4px)',
                          },
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            sx={{
                              fontSize: '0.9375rem',
                              fontWeight: 600,
                              color: colors.ink,
                              lineHeight: 1.3,
                              mb: item.sublabel ? 0.25 : 0,
                            }}
                          >
                            {item.label}
                          </Typography>
                          {item.sublabel && (
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: '0.75rem',
                                color: colors.inkSubtle,
                                lineHeight: 1.2,
                              }}
                            >
                              {item.sublabel}
                            </Typography>
                          )}
                        </Box>
                        {item.badge && (
                          <Chip
                            label={item.badge}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.6875rem',
                              fontWeight: 700,
                              backgroundColor: colors.accentOrange,
                              color: colors.paper,
                              '& .MuiChip-label': {
                                px: 1,
                              },
                            }}
                          />
                        )}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
    </Box>
  );
}

export default LandingMegaMenu;
